import React, { useState, ReactElement } from 'react';
import InteractiveMap from './InteractiveMap';
import VoterCard from './VoterCard';
import client from '../api/client';
import { useAuth } from '../context/AuthContext';
import type { Route, KnockbackCreate, StopStatus } from '../types';

interface RouteViewerProps {
  route: Route | null;
  onLogKnockback?: (knockback: KnockbackCreate) => void;
  onBack?: () => void;
}

export const RouteViewer: React.FC<RouteViewerProps> = ({
  route = null,
  onLogKnockback,
  onBack,
}): ReactElement => {
  const [currentStopIndex, setCurrentStopIndex] = useState<number>(0);
  const [downloading, setDownloading] = useState<boolean>(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const { user } = useAuth();

  // PII_READ_ROLES: admin, manager, canvasser (coordinator role is 'manager' in frontend)
  const piiReadRoles = new Set(['admin', 'manager', 'canvasser']);
  const canDownload = user && piiReadRoles.has(user.role);

  if (!route || !route.stops) {
    return (
      <div className="flex items-center justify-center h-full text-fg-muted">
        <p>No route selected</p>
      </div>
    );
  }

  const currentStop = route.stops[currentStopIndex];
  const currentVoter = currentStop?.voter;
  const completedStops = route.stops.filter((s) => s.status !== 'pending').length;
  const completionPercent = Math.round((completedStops / route.stops.length) * 100);

  const goToNextStop = (): void => {
    if (currentStopIndex < route.stops!.length - 1) {
      setCurrentStopIndex(currentStopIndex + 1);
    }
  };

  const goToPreviousStop = (): void => {
    if (currentStopIndex > 0) {
      setCurrentStopIndex(currentStopIndex - 1);
    }
  };

  const goToStop = (index: number): void => {
    setCurrentStopIndex(index);
  };

  const getStopStatusColor = (status: StopStatus): string => {
    const colors: Record<StopStatus, string> = {
      pending: 'text-fg-muted',
      visited: 'text-status-success',
      not_home: 'text-status-warning',
      refused: 'text-status-danger',
      moved: 'text-fg-muted',
      come_back: 'text-tier-warm',
    };
    return colors[status] || 'text-fg-muted';
  };

  const getStopStatusIcon = (status: StopStatus): string => {
    const icons: Record<StopStatus, string> = {
      pending: '○',
      visited: '✓',
      not_home: '✗',
      refused: '✗',
      moved: '⊘',
      come_back: '↻',
    };
    return icons[status] || '?';
  };

  const downloadRouteCsv = async (): Promise<void> => {
    if (!route) return;
    setDownloading(true);
    setDownloadError(null);
    try {
      const response = await client.get(`/routes/${route.id}/export/csv`, {
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: 'text/csv' });
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const now = new Date();
      const dateStr = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;
      link.href = blobUrl;
      link.download = `route-${route.id}-${dateStr}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      const errorMsg = (err as any)?.response?.data?.detail || 'Failed to download CSV';
      setDownloadError(errorMsg);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="grid grid-cols-3 grid-rows-1 gap-4 h-full min-h-0">
      {/* Map (left/top) - takes 2 columns */}
      <div className="col-span-2 min-h-0 rounded-lg overflow-hidden border border-border-base">
        <InteractiveMap
          voters={route.stops.map((s) => s.voter)}
          selectedRoute={route}
          onVoterClick={(voter) => {
            const stopIndex = route.stops!.findIndex((s) => s.voter.id === voter.id);
            if (stopIndex !== -1) {
              goToStop(stopIndex);
            }
          }}
        />
      </div>

      {/* Right sidebar with stops list. overflow-y-auto is the fallback scroll
          when even the pinned cards exceed the available height (short screens);
          on taller screens the inner stops list scrolls and this never engages. */}
      <div className="flex flex-col gap-4 min-h-0 overflow-y-auto">
        {/* Route stats header */}
        <div className="bg-bg-raised border border-border-base rounded-lg p-4">
          <h2 className="text-lg font-bold text-fg-primary mb-3">{route.name}</h2>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between items-center">
              <span className="text-fg-muted">Doors:</span>
              <span className="text-fg-primary font-bold">{route.door_count}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-fg-muted">Est. Time:</span>
              <span className="text-fg-primary font-bold">
                {route.est_hours.toFixed(1)}h
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-fg-muted">Completed:</span>
              <span className="text-fg-primary font-bold">{completionPercent}%</span>
            </div>
            <div className="mt-3">
              <div className="w-full bg-bg-elevated rounded-full h-2">
                <div
                  className="bg-accent h-2 rounded-full transition-all"
                  style={{ width: `${completionPercent}%` }}
                ></div>
              </div>
            </div>
          </div>

          <div className="mt-4 space-y-2">
            {route.google_maps_url && (
              <a
                href={route.google_maps_url}
                target="_blank"
                rel="noopener noreferrer"
                className="
                  block text-center px-3 py-2 bg-status-info-bg hover:bg-status-info-bg/80
                  text-white text-sm font-bold rounded transition-colors
                "
              >
                Driving directions (Google Maps)
              </a>
            )}
            {canDownload && (
              <div>
                <button
                  onClick={downloadRouteCsv}
                  disabled={downloading}
                  className="
                    w-full px-3 py-2 bg-bg-elevated hover:bg-status-success-bg disabled:opacity-50
                    disabled:cursor-not-allowed text-fg-primary text-sm font-bold rounded
                    transition-colors
                  "
                >
                  {downloading ? '⬇ Downloading...' : '⬇ Download CSV'}
                </button>
                {downloadError && (
                  <p className="text-status-danger text-xs mt-2">{downloadError}</p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Current stop viewer */}
        {currentVoter && (
          <div>
            <p className="text-fg-muted text-xs font-semibold mb-2">
              Stop {currentStopIndex + 1} of {route.stops.length}
            </p>
            <VoterCard
              voter={currentVoter}
              onLogVisit={
                onLogKnockback && currentStop
                  ? () =>
                      onLogKnockback({
                        route_stop_id: currentStop.id,
                        outcome: 'talked',
                      })
                  : undefined
              }
              isActive={true}
            />

            {/* Navigation buttons */}
            <div className="flex gap-2 mt-3">
              <button
                onClick={goToPreviousStop}
                disabled={currentStopIndex === 0}
                className="
                  flex-1 px-3 py-2 bg-bg-elevated hover:bg-border-subtle disabled:opacity-50
                  disabled:cursor-not-allowed text-fg-primary text-sm font-bold rounded
                  transition-colors
                "
              >
                ← Previous
              </button>
              <button
                onClick={goToNextStop}
                disabled={currentStopIndex === route.stops.length - 1}
                className="
                  flex-1 px-3 py-2 bg-bg-elevated hover:bg-border-subtle disabled:opacity-50
                  disabled:cursor-not-allowed text-fg-primary text-sm font-bold rounded
                  transition-colors
                "
              >
                Next →
              </button>
            </div>
          </div>
        )}

        {/* Scrollable stops list */}
        <div className="flex-1 overflow-y-auto min-h-0">
          <h3 className="text-sm font-bold text-fg-secondary mb-2 sticky top-0 bg-bg-base py-2">
            All Stops
          </h3>
          <div className="space-y-1">
            {route.stops.map((stop, idx) => (
              <button
                key={stop.id}
                onClick={() => goToStop(idx)}
                className={`
                  w-full text-left px-2 py-2 rounded text-xs transition-colors
                  ${
                    idx === currentStopIndex
                      ? 'bg-accent text-white'
                      : 'bg-bg-elevated hover:bg-border-subtle text-fg-primary'
                  }
                `}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1 truncate">
                    <div className="font-semibold truncate">
                      #{stop.sequence}: {stop.voter.first_name} {stop.voter.last_name}
                    </div>
                    <div className="text-fg-secondary text-xs truncate">
                      {stop.voter.address}
                    </div>
                  </div>
                  <span
                    className={`
                      ml-2 font-bold text-sm
                      ${getStopStatusColor(stop.status)}
                    `}
                  >
                    {getStopStatusIcon(stop.status)}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RouteViewer;
